import { BillingAttachment } from '@/lib/api/common/interfaces/billing.interface'
import {
    removeBillingAttachment,
    uploadBillingAttachment,
} from '@/lib/api/financial/financial-billing-api'
import ReceiptPreviewModal from '@/components/billing/receipt-preview-modal'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { createRef, useEffect, useRef, useState } from 'react'
import {
    MdAdd,
    MdClose,
    MdHourglassTop,
    MdLink,
    MdUploadFile,
    MdWarning,
} from 'react-icons/md'
import { toast } from 'react-toastify'

interface BillingAttachmentsPanelProps {
    billingId: number
    attachments?: BillingAttachment[] | null
    /** Recarrega a entidade dona da cobrança após upload/remoção. */
    onChanged: () => void | Promise<void>
    /** Alerta de comprovante obrigatório (cobrança pendente e sem anexo). */
    showProofWarning?: boolean
}

/**
 * Lista e gerencia os anexos de uma cobrança. O anexo é o comprovante exigido
 * para a baixa manual, então este painel é reaproveitado tanto na tela da
 * cobrança quanto nas telas que apenas exibem a cobrança (exame, consulta,
 * procedimento, teleconsulta, pedido) via `BillingAttachmentsModal`.
 *
 * Anexar é liberado a qualquer usuário autenticado (o vendedor que fecha a venda
 * costuma não ter permissão no módulo financeiro). Remover exige
 * `financial:billings:update`, para o comprovante não sumir da cobrança.
 */
export function BillingAttachmentsPanel({
    billingId,
    attachments,
    onChanged,
    showProofWarning = false,
}: BillingAttachmentsPanelProps) {
    const { canUpdate: canRemove } = useResourcePermissions(
        'financial',
        'billings'
    )

    const [showForm, setShowForm] = useState(false)
    const [file, setFile] = useState<File | null>(null)
    const [description, setDescription] = useState('')
    const [saving, setSaving] = useState(false)
    const [pendingApproval, setPendingApproval] = useState(false)
    const [preview, setPreview] = useState<BillingAttachment | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const list = attachments || []

    const resetForm = () => {
        setShowForm(false)
        setFile(null)
        setDescription('')
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    const handleAdd = async () => {
        if (!file) return

        setSaving(true)
        try {
            const response = await uploadBillingAttachment(
                billingId,
                file,
                description.trim() || undefined
            )

            if (response.success) {
                if (response.result?.receiptPendingApproval) {
                    setPendingApproval(true)
                    toast.success(
                        'Comprovante anexado: a baixa foi enviada para aprovação financeira'
                    )
                } else {
                    toast.success('Anexo adicionado')
                }
                resetForm()
                await onChanged()
            } else {
                toast.error(response.error || 'Erro ao adicionar anexo')
            }
        } catch {
            toast.error('Erro ao adicionar anexo')
        } finally {
            setSaving(false)
        }
    }

    const handleRemove = async (index: number) => {
        setSaving(true)
        try {
            const response = await removeBillingAttachment(billingId, index)

            if (response.success) {
                toast.success('Anexo removido')
                await onChanged()
            } else {
                toast.error(response.error || 'Erro ao remover anexo')
            }
        } catch {
            toast.error('Erro ao remover anexo')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-base-content flex items-center gap-2">
                    <MdLink className="text-xl" />
                    Anexos
                </h3>
                {!showForm && (
                    <button
                        type="button"
                        onClick={() => setShowForm(true)}
                        className="btn btn-sm btn-outline btn-secondary gap-1"
                    >
                        <MdAdd className="text-base" />
                        Adicionar
                    </button>
                )}
            </div>

            {showProofWarning && (
                <div className="alert alert-warning mb-4 py-2 text-sm">
                    <MdWarning className="text-lg shrink-0" />
                    <span>
                        Anexe o comprovante de pagamento para liberar a baixa da
                        cobrança.
                    </span>
                </div>
            )}

            {pendingApproval && (
                <div className="border border-info/30 bg-info/10 rounded-xl p-4 mb-4">
                    <div className="flex items-start gap-3">
                        <MdHourglassTop className="text-2xl text-info shrink-0 mt-0.5" />
                        <div>
                            <p className="font-semibold text-base-content">
                                Baixa enviada para aprovação
                            </p>
                            <p className="text-sm text-base-content/70 mt-1">
                                A IA vai ler o comprovante em segundo plano e
                                conferir valor, instituição emissora e sinais de
                                adulteração. Se a empresa tiver a aprovação
                                automática habilitada e o documento for
                                inequívoco, a cobrança é baixada e conciliada
                                sozinha; caso contrário, um operador financeiro
                                decide.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {showForm && (
                <div className="border border-base-300 rounded-lg p-4 mb-4 bg-base-200/20">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="form-control w-full">
                            <label className="label py-1">
                                <span className="label-text text-xs">
                                    Arquivo <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                                className="file-input file-input-bordered file-input-sm w-full"
                                onChange={e =>
                                    setFile(e.target.files?.[0] || null)
                                }
                            />
                            <label className="label py-0">
                                <span className="label-text-alt text-base-content/40">
                                    PDF, imagens, DOC, XLS (max. 10MB)
                                </span>
                            </label>
                        </div>
                        <div className="form-control w-full">
                            <label className="label py-1">
                                <span className="label-text text-xs">
                                    Descrição
                                </span>
                            </label>
                            <input
                                type="text"
                                placeholder="Ex: Comprovante de pagamento"
                                className="input input-bordered input-sm w-full"
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-3">
                        <button
                            type="button"
                            onClick={resetForm}
                            className="btn btn-sm btn-ghost gap-1"
                            disabled={saving}
                        >
                            <MdClose className="text-base" />
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={handleAdd}
                            className="btn btn-sm btn-secondary gap-1"
                            disabled={saving || !file}
                        >
                            {saving ? (
                                <span className="loading loading-spinner loading-xs"></span>
                            ) : (
                                <MdUploadFile className="text-base" />
                            )}
                            Enviar
                        </button>
                    </div>
                </div>
            )}

            {list.length > 0 ? (
                <div className="space-y-3">
                    {list.map((att, index) => (
                        <div
                            key={index}
                            className={`flex items-center gap-3 p-3 rounded-lg ${
                                att.rejected
                                    ? 'bg-error/5 border border-error/20'
                                    : 'bg-base-200/30'
                            }`}
                        >
                            <MdLink
                                className={`text-xl flex-shrink-0 ${
                                    att.rejected
                                        ? 'text-base-content/40'
                                        : 'text-secondary'
                                }`}
                            />
                            <div className="min-w-0 flex-1">
                                <button
                                    type="button"
                                    onClick={() => setPreview(att)}
                                    className={`font-medium hover:underline text-left ${
                                        att.rejected
                                            ? 'text-base-content/50 line-through'
                                            : 'text-secondary'
                                    }`}
                                >
                                    {att.name || att.url}
                                </button>
                                {att.rejected && (
                                    <p className="text-sm text-error mt-0.5">
                                        Comprovante rejeitado
                                        {att.rejectedReason
                                            ? `: ${att.rejectedReason}`
                                            : ''}
                                    </p>
                                )}
                                {att.description && (
                                    <p className="text-sm text-base-content/60">
                                        {att.description}
                                    </p>
                                )}
                            </div>
                            {canRemove && (
                                <button
                                    type="button"
                                    onClick={() => handleRemove(index)}
                                    className="btn btn-sm btn-ghost btn-square text-error"
                                    title="Remover anexo"
                                    disabled={saving}
                                >
                                    <MdClose className="text-base" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                !showForm && (
                    <p className="text-sm text-base-content/50">
                        Nenhum anexo adicionado.
                    </p>
                )
            )}

            {preview && (
                <ReceiptPreviewModal
                    isOpen
                    onClose={() => setPreview(null)}
                    url={preview.url}
                    name={preview.name}
                />
            )}
        </div>
    )
}

interface BillingAttachmentsModalProps extends BillingAttachmentsPanelProps {
    isOpen: boolean
    onClose: () => void
    title?: string
}

export function BillingAttachmentsModal({
    isOpen,
    onClose,
    title = 'Comprovante da cobrança',
    ...panelProps
}: BillingAttachmentsModalProps) {
    const modalRef = createRef<HTMLDialogElement>()

    useEffect(() => {
        if (isOpen) {
            modalRef.current?.showModal()
        } else {
            modalRef.current?.close()
        }
    }, [isOpen])

    return (
        <dialog className="modal z-50" ref={modalRef} onClose={onClose}>
            <div className="modal-box !max-w-2xl !mx-4 !p-0">
                <div className="flex items-center justify-between p-4 border-b border-base-300">
                    <h2 className="text-lg font-bold">{title}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn btn-sm btn-ghost btn-square"
                    >
                        <MdClose className="text-lg" />
                    </button>
                </div>

                <div className="p-6">
                    <BillingAttachmentsPanel {...panelProps} />
                </div>

                <div className="flex justify-end p-4 border-t border-base-300">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn btn-outline-light !rounded-xl"
                    >
                        Fechar
                    </button>
                </div>
            </div>

            <form method="dialog" className="modal-backdrop">
                <button onClick={onClose}>close</button>
            </form>
        </dialog>
    )
}
